import { Ionicons } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { Link, router, useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";
import { useDispatch } from "react-redux";

import Loader from "@/components/Loader";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import InputChooseList from "@/components/input/InputChooseList";
import InputDropList from "@/components/input/InputDropList";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import { CryptoProfileRequest } from "@/utils/dto/accounts.request";
import {
  ApplicationStateApplicationType,
  CounterPartyProfileFIATUSRelationshipToCustomer,
} from "@/utils/dto/extracted_enums";

type FormData = {
  profileType: string;
  relationshipToCustomer: string;
  name: string;
  countryCode: string;
};

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

export default function CryptoProfile() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);
  const [currentTransactionId, setCurrentTransactionId] = useState("");
  const [countries, setCountries] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);

  const resolver = classValidatorResolver(CryptoProfileRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, reset, setValue, setFocus } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  useFocusEffect(
    React.useCallback(() => {
      if (
        currentTransactionId !== globalValues.currentTransactionId ||
        globalValues.currentTransactionId === ""
      ) {
        reset();
        setCurrentTransactionId(globalValues.currentTransactionId);
      }
      return () => {};
    }, [globalValues.currentTransactionId, currentTransactionId]),
  );

  const fetchLocalization = async (route: string, term: string) => {
    try {
      const request = await sendRequest(
        "get",
        route,
        { searchTerm: term },
        true,
      );
      const mappedCountries = request.map((country: CountryData) => ({
        value: country.country,
        code: country.code,
        uniqueKey: `country-${country.code}`,
      }));
      setCountries(mappedCountries);
    } catch (error: any) {
      console.log(error);
    }
  };

  const onSubmit = (data: FormData) => {
    const cryptoProfile = { ...data };
    dispatch(setForm({ cryptoProfile }));
    router.push("/home/transaction/crypto/wallet");
  };

  const handleCountry = (data: { value: string; code: string }) => {
    setValue("countryCode", data.code, { shouldValidate: true });
    setFocus("relationshipToCustomer");
    setCountries([]);
  };

  return (
    <ScrollView contentContainerClassName="flex-grow items-center bg-white1 p-4 lg:p-0">
      <Link href="/home/transaction/register" asChild>
        <View className="absolute top-0 left-0 m-1.5 z-50">
          <Ionicons name="chevron-back-outline" size={28} color="#BDBDBD" />
        </View>
      </Link>
      <Link href="/home" asChild>
        <View className="absolute top-0 right-0 m-1 z-50">
          <Ionicons name="close-outline" size={32} color="#BDBDBD" />
        </View>
      </Link>
      <View className="my-2">
        <Title text={t("home/profile:titleinformation")} />
      </View>
      <View className="z-50 flex-grow flex-col w-full items-center justify-center">
        <View className="z-30 flex-col mx-4">
          <Input
            label={t("home/profile:input.name")}
            control={control}
            name="name"
          />
          <View className="z-50">
            <InputChoose
              label={t("home/profile:input.profiletype")}
              control={control}
              name="profileType"
              data={{ ApplicationStateApplicationType }}
            />
          </View>
          <View className="z-20">
            <InputDropList
              label={t("home/profile:input.country")}
              handleChange={(data) => {
                handleCountry(data);
              }}
              clearValueFunction={() => {
                setCountries([]);
              }}
              control={control}
              name="countryCode"
              data={countries}
              onSearchChange={(term) => fetchLocalization("/countries", term)}
              placeholder={t("common:placeholder.country")}
            />
          </View>
          <View className="z-10">
            <InputChooseList
              label={t("home/profile:input.relationshiptocustomer")}
              control={control}
              name="relationshipToCustomer"
              data={{ CounterPartyProfileFIATUSRelationshipToCustomer }}
            />
          </View>
        </View>
      </View>
      <Button
        label={t("common:button.nextstep")}
        onClick={handleSubmit(onSubmit)}
      />
    </ScrollView>
  );
}
